/**
 * UserDetailPanel — the LF-admin record card for a firm-side Contact
 * (Dashboard Slice I — ADR-0090). New panel introduced alongside the Firm
 * Detail Panel; mirrors its chrome (slide-in, role=dialog, Escape, click-
 * outside) so the operator's mental model is one panel idiom across the
 * Client Success roster.
 *
 * The panel answers two things:
 *   1. Who is this person? — name, role, status, notifications, email, phone,
 *      linked firm.
 *   2. What's outstanding about them? — open tasks pointed at this user,
 *      plus the trailing 7-day closed list as short-term memory.
 *
 * `+ Ask` in the footer opens the CreateTaskModal pre-bound to this user
 * (`initialAbout: { kind: "user", id }`), same pattern as the Firm panel.
 * Edit is rendered inert — like the Firm panel's Edit, the write surface for
 * user metadata lands in a later phase. The one wired write is the attorney
 * good-standing certificate (issue #416) — upload / replace / delete in the
 * Documents section, with the server-stamped on-file date.
 *
 * Spec: ADR-0090. Panel chrome conventions: see firm-detail-panel.tsx (the
 * shared shape — kept as a parallel implementation rather than a base class
 * because the two panels' content trees are different enough that an
 * abstraction earns its keep at a third panel, not a second).
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Check, Trash2, Upload, X } from "lucide-react";
import type { ClientSuccessUserDetail, FirmFile } from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { PanelTaskList } from "@/components/panel-task-list";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/format";

/** Status → badge variant. Active emerald; Inactive muted (greyed-out role). */
function statusVariant(s: string | null): "emerald" | "muted" {
  return s === "Inactive" ? "muted" : "emerald";
}

/** Titled section block — same shape as firm-detail-panel's Section. */
function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <section
      className="border-t px-5 py-4 first:border-t-0"
      data-testid={testId}
    >
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Label/value row — same shape as firm-detail-panel's Detail. */
function Detail({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <dt className="w-28 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{value}</dd>
    </div>
  );
}

export function UserDetailPanel({
  user,
  onClose,
  onAsk,
  onUploadGoodStanding,
  onDeleteGoodStanding,
  goodStandingPending = false,
  goodStandingError = null,
  testId = "user-detail-panel",
}: {
  user: ClientSuccessUserDetail;
  onClose: () => void;
  /** Open the create-task modal pre-bound to this user. Omitted when the
   *  parent has no wiring — the footer button doesn't render. */
  onAsk?: () => void;
  /** Upload (or replace) the good-standing certificate. Omitted → the row is
   *  read-only (✓/✗ only, no affordances). Issue #416. */
  onUploadGoodStanding?: (file: File) => void;
  /** Clear the good-standing certificate. Omitted with onUpload → read-only. */
  onDeleteGoodStanding?: () => void;
  /** A good-standing upload/delete is in flight. */
  goodStandingPending?: boolean;
  /** Human-readable error from the last good-standing op, or null. */
  goodStandingError?: string | null;
  testId?: string;
}) {
  // Slide-in: start off-screen, flip in after first paint (same idiom as
  // FirmDetailPanel — see its comment for why a raf is needed).
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="presentation"
      onMouseDown={onBackdropClick}
      data-testid={`${testId}-overlay`}
    >
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-[#000527] transition-opacity duration-200 ${
          mounted ? "opacity-40" : "opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`User detail for ${user.name}`}
        className={`relative ml-auto flex h-full w-full max-w-[580px] flex-col border-l bg-card text-card-foreground shadow-xl transition-transform duration-200 ${
          mounted ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid={testId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted text-xs font-semibold uppercase text-muted-foreground"
              data-testid={`${testId}-avatar`}
            >
              {initials(user.name)}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-base font-semibold">{user.name}</span>
              <div className="flex flex-wrap items-center gap-2">
                {user.status ? (
                  <Badge variant={statusVariant(user.status)}>
                    {user.status}
                  </Badge>
                ) : null}
                {user.role ? (
                  <Badge variant="muted" data-testid={`${testId}-role`}>
                    {user.role}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close user detail"
            data-testid={`${testId}-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <Section title="Contact" testId={`${testId}-contact`}>
            <dl className="flex flex-col gap-2">
              <Detail
                label="Email"
                value={
                  user.email ? (
                    <span
                      className="break-all"
                      data-testid={`${testId}-email`}
                    >
                      {user.email}
                    </span>
                  ) : (
                    <span
                      className="italic text-muted-foreground"
                      data-testid={`${testId}-email-missing`}
                    >
                      Not on file
                    </span>
                  )
                }
              />
              <Detail
                label="Phone"
                value={
                  user.phone ? (
                    <span data-testid={`${testId}-phone`}>{user.phone}</span>
                  ) : (
                    <span
                      className="italic text-muted-foreground"
                      data-testid={`${testId}-phone-missing`}
                    >
                      Not on file
                    </span>
                  )
                }
              />
              {user.position ? (
                <Detail
                  label="Position"
                  value={
                    <span data-testid={`${testId}-position`}>
                      {user.position}
                    </span>
                  }
                />
              ) : null}
              {user.notifications.length > 0 ? (
                <Detail
                  label="Notifications"
                  value={
                    <span data-testid={`${testId}-notifications`}>
                      {user.notifications.join(", ")}
                    </span>
                  }
                />
              ) : null}
            </dl>
          </Section>

          <Section title="Linked firm" testId={`${testId}-firm`}>
            {user.firmName ? (
              <p
                className="text-sm font-medium text-foreground"
                data-testid={`${testId}-firm-name`}
              >
                {user.firmName}
              </p>
            ) : (
              <p
                className="text-sm italic text-muted-foreground"
                data-testid={`${testId}-firm-missing`}
              >
                Not linked to a firm.
              </p>
            )}
          </Section>

          <Section title="Documents" testId={`${testId}-documents`}>
            {goodStandingError ? (
              <p
                className="mb-2 text-sm text-error"
                data-testid={`${testId}-good-standing-error`}
                role="status"
              >
                {goodStandingError}
              </p>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              <GoodStandingRow
                file={user.goodStandingFile}
                date={user.goodStandingDate}
                pending={goodStandingPending}
                onUpload={onUploadGoodStanding}
                onDelete={onDeleteGoodStanding}
                testId={`${testId}-good-standing`}
              />
            </ul>
          </Section>

          <Section title="Tasks" testId={`${testId}-tasks`}>
            <PanelTaskList
              openTasks={user.tasks}
              closedTasks={user.recentlyClosedTasks}
              testId={`${testId}-tasks`}
              emptyLabel="No tasks about this user."
            />
          </Section>
        </div>

        <footer
          className="flex items-center gap-2 border-t px-5 py-3"
          data-testid={`${testId}-actions`}
        >
          <InertButton label="Edit" testId={`${testId}-edit`} />
          {onAsk ? (
            <button
              type="button"
              onClick={onAsk}
              data-testid={`${testId}-ask`}
              className="rounded-md border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              + Ask
            </button>
          ) : null}
        </footer>
      </aside>
    </div>
  );
}

/**
 * The attorney good-standing certificate row (issue #416) — a single-document
 * slot on a user. Green ✓ + filename link + "Provided {date}" when present;
 * muted ✗ + "Not on file" when absent. When wired, Replace (upload) is always
 * available and Delete asks for inline confirmation; with no callbacks the row
 * is the read-only ✓/✗ view. A deliberately tiny bespoke row: the firm
 * DocumentRow is keyed by a firm-specific slot union, so reusing it would
 * couple the user panel to that type — this avoids the coupling.
 */
function GoodStandingRow({
  file,
  date,
  pending,
  onUpload,
  onDelete,
  testId,
}: {
  file: FirmFile | null;
  date: string | null;
  pending: boolean;
  onUpload?: (file: File) => void;
  onDelete?: () => void;
  testId: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const present = file !== null;
  const wired = !!onUpload && !!onDelete;

  function openPicker() {
    if (!wired || pending) return;
    inputRef.current?.click();
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f && onUpload) onUpload(f);
  }

  return (
    <li
      className={cn(
        "group relative flex items-center gap-3 rounded-sm py-1 text-sm",
        wired && !present && !pending && "cursor-pointer hover:bg-accent/40",
        pending && "opacity-60",
      )}
      data-testid={testId}
      data-present={present ? "true" : "false"}
      data-pending={pending ? "true" : "false"}
      onClick={!present ? openPicker : undefined}
    >
      {present ? (
        <Check
          className="h-4 w-4 shrink-0 text-success"
          aria-label="On file"
        />
      ) : (
        <X
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-label="Not on file"
        />
      )}
      <span className="w-44 shrink-0 text-muted-foreground">Good standing</span>
      {present && file ? (
        <span className="flex min-w-0 items-center gap-2">
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 truncate font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
            data-testid={`${testId}-link`}
            onClick={(e) => e.stopPropagation()}
          >
            {file.name}
          </a>
          {date ? (
            <span
              className="shrink-0 text-xs text-muted-foreground"
              data-testid={`${testId}-date`}
            >
              Provided {date}
            </span>
          ) : null}
        </span>
      ) : (
        <span
          className="italic text-muted-foreground"
          data-testid={`${testId}-missing`}
        >
          {pending ? "Uploading…" : "Not on file"}
        </span>
      )}

      {wired ? (
        confirmingDelete ? (
          <span
            className="ml-auto flex shrink-0 items-center gap-1 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-muted-foreground">Remove?</span>
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(false);
                onDelete?.();
              }}
              disabled={pending}
              className="rounded px-1.5 py-1 font-medium text-destructive hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-delete-confirm`}
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="rounded px-1.5 py-1 text-muted-foreground hover:bg-accent"
              data-testid={`${testId}-delete-cancel`}
            >
              Cancel
            </button>
          </span>
        ) : (
          <div className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
              disabled={pending}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={
                present
                  ? "Replace good-standing certificate"
                  : "Upload good-standing certificate"
              }
              data-testid={`${testId}-upload`}
            >
              <Upload className="h-3.5 w-3.5" />
            </button>
            {present ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingDelete(true);
                }}
                disabled={pending}
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Delete good-standing certificate"
                data-testid={`${testId}-delete`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        )
      ) : null}

      {wired ? (
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onFileSelected}
          data-testid={`${testId}-input`}
        />
      ) : null}
    </li>
  );
}

/** Rendered-but-inert action button — same shape as firm-detail-panel's. */
function InertButton({ label, testId }: { label: string; testId: string }) {
  return (
    <button
      type="button"
      disabled
      title="Not wired yet — actions land in a later phase"
      data-testid={testId}
      className={cn(
        "cursor-not-allowed rounded-md border px-3 py-1.5 text-sm font-medium text-muted-foreground opacity-60",
      )}
    >
      {label}
    </button>
  );
}
